/**
 * EliseAI Schedule Tour (IFrame) Block
 *
 * Embeds EliseAI's tour scheduling page in an iframe.
 * The URL defaults to the property's custom_eliseai_tour_scheduler_url field.
 */

import { useState, useEffect } from 'react';
import type { BlockDefinition, BlockProps } from '../../types';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useBlockToken, EditableBlockToken } from '@vibesites/runtime/react';

const MOBILE_BREAKPOINT = 768;
const DEFAULT_HEIGHT = '1700';
const DEFAULT_MOBILE_HEIGHT = '1400';

function iframeHeightPx(value: string | null, fallback: string): string {
  const raw = (value || fallback).trim();
  return raw.endsWith('px') ? raw : `${raw}px`;
}

// ============================================================================
// Component
// ============================================================================

function EliseScheduleTourIframeComponent({ sectionId: _sectionId }: BlockProps) {
  const url = useBlockToken('url', {
    defaultField: 'custom_eliseai_tour_scheduler_url',
  });
  const height = useBlockToken('height', { defaultValue: DEFAULT_HEIGHT });
  const mobileHeight = useBlockToken('height_mobile', { defaultValue: DEFAULT_MOBILE_HEIGHT });
  const hideScrollBars = useBlockToken('hide_scroll_bars', { defaultValue: 'true' }) === 'true';

  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < MOBILE_BREAKPOINT : false
  );

  useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    setIsMobile(mql.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  if (!url) {
    return (
      <div
        className="flex items-center justify-center py-16 text-center bg-muted"
        style={{ color: 'var(--vibe-color-text-muted)' }}
      >
        <div>
          <EditableBlockToken
            localName="url"
            type="url"
            label="Tour Scheduler URL"
            as="p"
            className="text-lg font-medium"
            style={{ fontFamily: 'var(--vibe-font-heading)' }}
          >
            EliseAI Schedule Tour
          </EditableBlockToken>
          <p className="text-sm mt-1">
            Configure the EliseAI tour scheduler URL to display the scheduling form.
          </p>
        </div>
      </div>
    );
  }

  const iframeHeight = iframeHeightPx(
    isMobile ? mobileHeight : height,
    isMobile ? DEFAULT_MOBILE_HEIGHT : DEFAULT_HEIGHT,
  );

  return (
    <div className="w-full" style={{ overflow: hideScrollBars ? 'hidden' : undefined }}>
      <iframe
        src={url}
        title="Schedule a Tour"
        width="100%"
        height={iframeHeight}
        scrolling={hideScrollBars ? 'no' : undefined}
        style={{
          border: 'none',
          display: 'block',
          width: '100%',
          height: iframeHeight,
          overflow: hideScrollBars ? 'hidden' : undefined,
        }}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        loading="lazy"
      />
    </div>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

const contentSchema: ComponentContentSchema = {
  slot: 'EliseScheduleTourIframe',
  displayName: 'EliseAI Schedule Tour (IFrame)',
  tokens: {
    url: {
      label: 'Tour Scheduler URL',
      type: 'url',
      defaultField: 'custom_eliseai_tour_scheduler_url',
      suggestedFields: ['custom_eliseai_tour_scheduler_url'],
      description: 'The EliseAI tour scheduling page URL. Mapped to the property field by default.',
      required: true,
    },
    height: {
      label: 'Height (px)',
      type: 'text',
      defaultValue: DEFAULT_HEIGHT,
      description: 'Height of the iframe in pixels (desktop).',
    },
    height_mobile: {
      label: 'Mobile Height (px)',
      type: 'text',
      defaultValue: DEFAULT_MOBILE_HEIGHT,
      description: 'Height of the iframe on mobile screens (under 768px). Set taller to accommodate stacked form elements.',
    },
    hide_scroll_bars: {
      label: 'Hide Scrollbars',
      type: 'text',
      defaultValue: 'true',
      description: 'When on, the iframe cannot scroll and overflowing content is clipped.',
    },
  },
};

// ============================================================================
// Block Definition
// ============================================================================

export const eliseScheduleTourIframeBlock: BlockDefinition = {
  id: 'elise-schedule-tour-iframe',
  name: 'EliseAI Schedule Tour (IFrame)',
  description: 'Embedded EliseAI tour scheduling page. Automatically maps to the property\'s EliseAI tour scheduler URL.',
  category: 'Integrations',
  component: EliseScheduleTourIframeComponent,
  contentSchema,
  tags: ['elise', 'tour', 'schedule', 'scheduling', 'iframe', 'leasing'],
  defaultGridSpan: 12,
};
